import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Badge,
  Spinner,
  MessageBar,
  MessageBarBody,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { OpenRegular, ArrowDownloadRegular, DeleteRegular, ArrowSyncRegular } from '@fluentui/react-icons';
import type { FilingDetail } from '../../types';
import {
  useManualFilings,
  useDeleteManualFiling,
  downloadDataverseFilingDocument,
  downloadManualFilingDocument,
  stateTaxClientKeys,
} from '../../services/stateTaxClientsApi';
import { useReopenFiling, taskKeys } from '../../services/complianceApi';
import { useQueryClient } from '@tanstack/react-query';
import { formatCurrency, formatWholeDay } from '../../utils/complianceFormatters';
import { canReopen } from '../../utils/filingUtils';
import ReopenChoiceDialog from '../../components/compliance/ReopenChoiceDialog';
import FilingVersionHistory from '../../components/compliance/FilingVersionHistory';

/**
 * Read-only detail view for a single filing — opened by clicking a filing-activity row or a
 * filed recent-history cell. Unifies the two PDF sources: a Dataverse-derived filing streams
 * its task File column; a manual filing streams its blob attachment. Manual rows are enriched
 * from the manual-filings list (confirmation / amount / note / recordedBy +
 * whether it actually has a document) — the feed/cell only carries the identity + filename.
 *
 * For manual filings only, an "Un-file (remove)" button lets staff undo a mistaken entry.
 * Uses window.confirm (matching the notes-card delete pattern) — lightweight + no extra Dialog.
 *
 * For task-source filings: shows full inline detail (confirmation #, amount, bank debit,
 * filed date/by, status) + a Reopen affordance (only when status === 'filed') + the
 * FilingVersionHistory table when prior versions exist.
 */
const useStyles = makeStyles({
  rows: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  row: { display: 'grid', gridTemplateColumns: '120px 1fr', gap: tokens.spacingHorizontalM, alignItems: 'baseline' },
  label: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  value: { color: tokens.colorNeutralForeground1, fontSize: tokens.fontSizeBase300, wordBreak: 'break-word' },
  file: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '4px',
    padding: '6px 8px',
    wordBreak: 'break-all',
  },
  headerRow: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, marginBottom: tokens.spacingVerticalS },
  docActions: { display: 'flex', gap: tokens.spacingHorizontalS, marginTop: tokens.spacingVerticalS },
  versionHistorySection: { marginTop: tokens.spacingVerticalM },
  versionHistoryHeading: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    marginBottom: tokens.spacingVerticalXS,
  },
  successBar: { marginBottom: tokens.spacingVerticalS },
});

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stateTaxClientId: string;
  detail: FilingDetail | null;
}


export function FilingDetailModal({ open, onOpenChange, stateTaxClientId, detail }: Props) {
  const styles = useStyles();
  const { t } = useTranslation();
  const dash = t('common.dash');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [reopenOpen, setReopenOpen] = useState(false);

  const qc = useQueryClient();
  const reopenMutation = useReopenFiling();

  // Enrich a manual filing with its full record (confirmation / amount / note / recordedBy +
  // whether it actually has a document) — the feed/cell only carries the identity + filename.
  const { data: manualFilings } = useManualFilings(
    detail?.source === 'manual' ? stateTaxClientId : '',
  );
  const manual = detail?.source === 'manual'
    ? manualFilings?.find((m) => m.id === detail.manualFilingId)
    : undefined;

  const deleteMutation = useDeleteManualFiling();

  if (!detail) return null;

  const isManual = detail.source === 'manual';
  const isTask = detail.source === 'task';

  // Back-compat: a legacy single confirmation number surfaces as the return confirmation.
  const returnConfirmation = isManual
    ? (manual?.returnConfirmationNumber ?? detail.confirmationNumber ?? manual?.confirmationNumber)
    : undefined;
  const paymentConfirmation = isManual ? manual?.paymentConfirmationNumber : undefined;
  const amount = isManual ? (detail.amount ?? manual?.amount) : undefined;
  const note = isManual ? (detail.note ?? manual?.note) : undefined;
  const recordedBy = isManual ? (detail.recordedByName ?? manual?.recordedByName) : undefined;
  const fileName = detail.filename ?? (isManual ? manual?.attachmentFileName : undefined);
  const paymentFileName = isManual ? manual?.paymentAttachmentFileName : undefined;

  // Task-source filings: document button is only shown when a filename/attachment is present.
  // Full task-attachment download wiring is a TODO — the task-detail page is the canonical
  // document viewer for task filings. Show a clear affordance rather than a broken button.
  // TODO(W3): wire downloadFilingAttachment(taskId, obligationId, clientId) once confirmed endpoint works.
  const taskHasDocument = isTask && !!detail.filename;

  const hasDocument = detail.source === 'dataverse'
    ? !!detail.activityId && !!detail.slotField
    : isTask
      ? taskHasDocument
      : !!(manual?.attachmentBlobName ?? detail.filename);

  // The separate payment-confirmation PDF (split obligations only).
  const hasPaymentDocument = isManual && !!manual?.paymentAttachmentBlobName;

  // Whether the reopen affordance should be shown (task source + filed status only).
  const showReopen = isTask && canReopen(detail.status);

  async function openDoc(mode: 'view' | 'download', which: 'return' | 'payment' = 'return') {
    if (!detail) return;
    // For "view", open the tab synchronously inside the click handler. Opening it *after* the
    // awaited fetch lands outside the user-gesture window and gets swallowed by popup blockers
    // (the original cause of "View PDF does nothing"). We can't pass `noopener` here — that makes
    // window.open return null and we need the handle to point the tab at the blob once fetched;
    // blob URLs are same-origin so the opener reference is not a meaningful risk.
    // programmatic-nav: an authenticated document fetched as a blob; there is no URL a link could carry
    const viewTab = mode === 'view' ? window.open('', '_blank') : null;
    setBusy(true);
    setError(null);
    try {
      const wantInline = mode === 'view';
      // Task-source rows carry inline filing detail; PDF download via task attachment endpoint
      // is a TODO (see comment above). Guard so nothing breaks — hasDocument already gates
      // the button to only show when filename is present.
      if (detail.source === 'task') { setBusy(false); return; }
      const { blob, fileName: dlName } = detail.source === 'dataverse'
        ? await downloadDataverseFilingDocument(stateTaxClientId, detail.activityId!, detail.slotField!, detail.filename, wantInline)
        : await downloadManualFilingDocument(stateTaxClientId, detail.manualFilingId, wantInline, which);
      // For inline viewing the browser keys off the blob's MIME type — if the server
      // streamed it as application/octet-stream (or sent no type) the tab downloads
      // instead of rendering (the "View PDF downloads anyway" report). Coerce only the
      // generic cases to application/pdf so it renders inline; respect any specific type
      // the server did send. Download path keeps the original blob — type is moot there
      // since the anchor's download attr forces a save.
      const isGenericType = blob.type === '' || blob.type === 'application/octet-stream';
      const viewBlob =
        mode === 'view' && isGenericType ? new Blob([blob], { type: 'application/pdf' }) : blob;
      const url = URL.createObjectURL(viewBlob);
      if (mode === 'view') {
        if (viewTab) {
          // programmatic-nav: an authenticated document fetched as a blob; there is no URL a link could carry
          viewTab.location.href = url;
        } else {
          // Tab was blocked despite the synchronous open — best-effort fallback.
          // programmatic-nav: an authenticated document fetched as a blob; there is no URL a link could carry
          window.open(url, '_blank', 'noopener');
        }
        // Give the tab time to load before releasing the object URL.
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = dlName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      }
    } catch {
      viewTab?.close();
      setError(t('filingDetail.downloadError'));
    } finally {
      setBusy(false);
    }
  }

  function handleUnfile() {
    if (!detail || detail.source !== 'manual') return;
    const ok = window.confirm(t('filingDetail.unfileConfirm'));
    if (!ok) return;
    setBusy(true);
    setError(null);
    deleteMutation.mutate(
      { stateTaxClientId, filingId: detail.manualFilingId },
      {
        onSuccess: () => {
          setBusy(false);
          onOpenChange(false);
        },
        onError: () => {
          setBusy(false);
          setError(t('filingDetail.unfileError'));
        },
      },
    );
  }

  function handleReopenChoose(mode: 'original' | 'amendment') {
    if (!detail || detail.source !== 'task') return;
    reopenMutation.mutate(
      { taskId: detail.taskId, obligationId: detail.obligationId, payload: { clientId: stateTaxClientId, mode } },
      {
        onSuccess: () => {
          setReopenOpen(false);
          // Invalidate filing-activity + task queries so list + modal data refresh.
          void qc.invalidateQueries({ queryKey: [...stateTaxClientKeys.all, 'filing-activity', stateTaxClientId] });
          void qc.invalidateQueries({ queryKey: taskKeys.all });
          setSuccessMsg(t('filingDetail.reopenSuccess', { defaultValue: 'Filing reopened. The task has been reset to in-progress.' }));
        },
        onError: () => {
          setError(t('filingDetail.reopenError', { defaultValue: 'Reopen failed — please try again or open the task directly.' }));
        },
      },
    );
  }

  // Badge colour for source.
  const badgeColor = isManual ? 'warning' : isTask ? 'success' : 'informative';
  const badgeLabel = isManual
    ? t('filingDetail.source.manual')
    : isTask
      ? t('filingDetail.source.task', { defaultValue: 'System (task)' })
      : t('filingDetail.source.dataverse');

  return (
    <>
      <Dialog open={open} onOpenChange={(_e, data) => onOpenChange(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t('filingDetail.title')}</DialogTitle>
            <DialogContent>
              <div className={styles.headerRow}>
                <Badge appearance="tint" color={badgeColor}>
                  {badgeLabel}
                </Badge>
                {isTask && (
                  <Badge appearance="outline" color="informative">
                    {/* i18n-ignore: a version tag reads the same in both languages */}
                    {`v${detail.versionNumber}`}
                  </Badge>
                )}
                {isTask && (
                  <Badge appearance="tint" color={detail.status === 'filed' ? 'success' : 'severe'}>
                    {detail.status}
                  </Badge>
                )}
              </div>

              {error && (
                <MessageBar intent="error" style={{ marginBottom: 8 }}>
                  <MessageBarBody>{error}</MessageBarBody>
                </MessageBar>
              )}
              {successMsg && (
                <MessageBar intent="success" className={styles.successBar}>
                  <MessageBarBody>{successMsg}</MessageBarBody>
                </MessageBar>
              )}

              <div className={styles.rows}>
                <Row label={t('filingDetail.fields.obligation')} value={detail.obligationType} />
                <Row label={t('filingDetail.fields.period')} value={detail.periodLabel} />
                <Row label={t('filingDetail.fields.filed')} value={detail.filedOn ? formatWholeDay(detail.filedOn) : dash} />

                {/* ── Task-source inline detail ───────────────────────────────── */}
                {isTask && (
                  <>
                    <Row
                      label={t('filingDetail.fields.returnConfirmation', { defaultValue: 'Confirmation #' })}
                      value={detail.confirmationNumber ?? dash}
                    />
                    <Row
                      label={t('filingDetail.fields.amount', { defaultValue: 'Amount due' })}
                      value={detail.amountDue != null ? formatCurrency(detail.amountDue) : dash}
                    />
                    {detail.bankDebitAmount != null && (
                      <Row
                        label={t('filingDetail.fields.bankDebit', { defaultValue: 'Bank debit' })}
                        value={[
                          formatCurrency(detail.bankDebitAmount),
                          detail.bankDebitAccountMasked,
                          detail.bankDebitDate ? formatWholeDay(detail.bankDebitDate) : undefined,
                        ].filter(Boolean).join(' · ')}
                      />
                    )}
                    {detail.filedBy && (
                      <Row label={t('filingDetail.fields.filedBy', { defaultValue: 'Filed by' })} value={detail.filedBy} />
                    )}
                  </>
                )}

                {/* ── Manual-source enriched detail ──────────────────────────── */}
                {isManual && (
                  <>
                    <Row label={t('filingDetail.fields.returnConfirmation')} value={returnConfirmation || dash} />
                    {paymentConfirmation && (
                      <Row label={t('filingDetail.fields.paymentConfirmation')} value={paymentConfirmation} />
                    )}
                    <Row
                      label={t('filingDetail.fields.amount')}
                      value={typeof amount === 'number' ? amount.toLocaleString(undefined, { style: 'currency', currency: 'USD' }) : dash}
                    />
                    {note && <Row label={t('filingDetail.fields.note')} value={note} />}
                    {recordedBy && <Row label={t('filingDetail.fields.recordedBy')} value={recordedBy} />}
                  </>
                )}

                {/* ── Document section ───────────────────────────────────────── */}
                <div className={styles.row}>
                  <Text className={styles.label}>
                    {hasPaymentDocument ? t('filingDetail.fields.returnDocument') : t('filingDetail.fields.document')}
                  </Text>
                  <div>
                    {isTask && taskHasDocument && (
                      // Task attachment download is not yet wired through this modal.
                      // Open the task directly to access the filing document.
                      // TODO(W3): wire downloadFilingAttachment endpoint here.
                      <>
                        {fileName && <div className={styles.file}>{fileName}</div>}
                        <Text className={styles.label} style={{ display: 'block', marginTop: 4 }}>
                          {t('filingDetail.taskDocHint', { defaultValue: 'Open the task to view or download this document.' })}
                        </Text>
                      </>
                    )}
                    {isTask && !taskHasDocument && (
                      <Text className={styles.value}>{t('filingDetail.noDocument')}</Text>
                    )}
                    {!isTask && hasDocument && (
                      <>
                        {fileName && <div className={styles.file}>{fileName}</div>}
                        <div className={styles.docActions}>
                          <Button
                            size="small"
                            appearance="primary"
                            icon={busy ? <Spinner size="tiny" /> : <OpenRegular />}
                            disabled={busy}
                            onClick={() => { void openDoc('view', 'return'); }}
                          >
                            {t('filingDetail.viewPdf')}
                          </Button>
                          <Button
                            size="small"
                            icon={<ArrowDownloadRegular />}
                            disabled={busy}
                            onClick={() => { void openDoc('download', 'return'); }}
                          >
                            {t('filingDetail.downloadPdf')}
                          </Button>
                        </div>
                      </>
                    )}
                    {!isTask && !hasDocument && (
                      <Text className={styles.value}>{t('filingDetail.noDocument')}</Text>
                    )}
                  </div>
                </div>

                {/* Separate payment-confirmation PDF (split obligations only). */}
                {hasPaymentDocument && (
                  <div className={styles.row}>
                    <Text className={styles.label}>{t('filingDetail.fields.paymentDocument')}</Text>
                    <div>
                      {paymentFileName && <div className={styles.file}>{paymentFileName}</div>}
                      <div className={styles.docActions}>
                        <Button
                          size="small"
                          appearance="primary"
                          icon={busy ? <Spinner size="tiny" /> : <OpenRegular />}
                          disabled={busy}
                          onClick={() => { void openDoc('view', 'payment'); }}
                        >
                          {t('filingDetail.viewPdf')}
                        </Button>
                        <Button
                          size="small"
                          icon={<ArrowDownloadRegular />}
                          disabled={busy}
                          onClick={() => { void openDoc('download', 'payment'); }}
                        >
                          {t('filingDetail.downloadPdf')}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* ── Version history (task source only, when amendments exist) ── */}
              {isTask && detail.priorVersions.length > 0 && (
                <div className={styles.versionHistorySection}>
                  <Text className={styles.versionHistoryHeading} block>
                    {t('filingDetail.versionHistory', { defaultValue: 'Amendment history' })}
                  </Text>
                  <FilingVersionHistory
                    currentVersion={detail.versionNumber}
                    current={{
                      confirmationNumber: detail.confirmationNumber,
                      amountDue: detail.amountDue,
                      bankDebitAmount: detail.bankDebitAmount,
                      bankDebitDate: detail.bankDebitDate,
                      bankDebitAccountMasked: detail.bankDebitAccountMasked,
                      filedAt: detail.filedOn,
                      filedBy: detail.filedBy,
                    }}
                    priorVersions={detail.priorVersions}
                  />
                </div>
              )}
            </DialogContent>
            <DialogActions>
              {isManual && (
                <Button
                  appearance="subtle"
                  style={{ color: tokens.colorPaletteRedForeground1, marginRight: 'auto' }}
                  icon={busy ? <Spinner size="tiny" /> : <DeleteRegular />}
                  disabled={busy}
                  onClick={handleUnfile}
                >
                  {t('filingDetail.unfile')}
                </Button>
              )}
              {showReopen && (
                <Button
                  appearance="subtle"
                  style={{ marginRight: 'auto' }}
                  icon={reopenMutation.isPending ? <Spinner size="tiny" /> : <ArrowSyncRegular />}
                  disabled={reopenMutation.isPending}
                  onClick={() => { setReopenOpen(true); }}
                  data-testid="reopen-trigger"
                >
                  {t('filingDetail.reopen', { defaultValue: 'Reopen' })}
                </Button>
              )}
              <Button appearance="secondary" onClick={() => onOpenChange(false)}>
                {t('filingDetail.close')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* ReopenChoiceDialog rendered outside the main Dialog to avoid nested dialog stacking */}
      {showReopen && (
        <ReopenChoiceDialog
          open={reopenOpen}
          onOpenChange={(o) => { if (!reopenMutation.isPending) setReopenOpen(o); }}
          isPending={reopenMutation.isPending}
          onChoose={handleReopenChoose}
        />
      )}
    </>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  const styles = useStyles();
  return (
    <div className={styles.row}>
      <Text className={styles.label}>{label}</Text>
      <Text className={styles.value}>{value}</Text>
    </div>
  );
}
